import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useProtectedResources } from './useProtectedResources';
// Protected-asset badge (shield). CRA/SVGR turns the .svg into a React
// component; rendered as a nested <svg> inside the topology canvas with
// explicit x/y/width/height. Source: /tmp/shield.svg, vendored here.
import { ReactComponent as ShieldIcon } from './shield.svg';

// Layout constants (lifted from aveq-app/src/NetworkMap.js — same
// hand-rolled tree algorithm, no external graph lib).
const NODE_RADIUS = 28;
const LEVEL_HEIGHT = 140;
const NODE_SPACING = 200;
export const POLL_INTERVAL_MS = 5000;
// Re-probe red nodes at most every PROBE_REFRESH_MS — slower than the
// topology poll so we don't hammer root-qbric with auto-probes every 5s
// (each probe is a real RPC across the mesh).
const PROBE_REFRESH_MS = 60000;

function computeLayout(node, depth = 0) {
  const result = { ...node, depth, children: [] };
  if (!node.children || node.children.length === 0) {
    result.x = 0;
    result.y = depth * LEVEL_HEIGHT;
    return result;
  }
  const childCount = node.children.length;
  const layoutChildren = node.children.map((c) => computeLayout(c, depth + 1));
  const totalWidth = (childCount - 1) * NODE_SPACING;
  layoutChildren.forEach((child, i) => { child.x = -totalWidth / 2 + i * NODE_SPACING; });
  result.children = layoutChildren;
  result.x = 0;
  result.y = depth * LEVEL_HEIGHT;
  return result;
}

function flattenTree(node, offsetX = 0) {
  const x = node.x + offsetX;
  const y = node.y;
  const nodes = [{ ...node, x, y }];
  const links = [];
  for (const child of (node.children || [])) {
    const cx = child.x + offsetX;
    const cy = child.y;
    links.push({ x1: x, y1: y, x2: cx, y2: cy });
    const sub = flattenTree(child, offsetX);
    nodes.push(...sub.nodes);
    links.push(...sub.links);
  }
  return { nodes, links };
}

// Walk the tree to find a subtree rooted at the given node id. Returns
// null if no such node exists. First half of the per-network filter:
// after finding the subtree, pruneToNetwork strips out descendants
// whose nodeId isn't in the network's membership set.
//
// Both ids coerced to string before compare: topology serializes
// `nodeId` as text (qbric does this to dodge JS's 2^53 precision
// ceiling for bigints).
function findSubtree(node, rootNodeId) {
  if (!node) return null;
  if (String(node.nodeId) === String(rootNodeId)) return node;
  for (const c of (node.children || [])) {
    const hit = findSubtree(c, rootNodeId);
    if (hit) return hit;
  }
  return null;
}

// Second half of the per-network filter. After findSubtree returns the
// candidate subtree, strip out any descendants whose nodeId isn't in
// `nodeIdSet`. Solves the "aveq-1 root is the global mesh root, so its
// subtree visually contains every other network's nodes" quirk —
// create_network_with_root_node links new network roots under the
// global root for mesh routability, but those roots' network_id is the
// new network's, not aveq-1's.
function pruneToNetwork(node, nodeIdSet) {
  if (!node || !nodeIdSet.has(String(node.nodeId))) return null;
  const kept = [];
  for (const c of (node.children || [])) {
    const p = pruneToNetwork(c, nodeIdSet);
    if (p) kept.push(p);
  }
  return { ...node, children: kept };
}

export default function TopologyView({ onSelect, selectedId, probedOkIds, onProbeOk, rootNodeId, nodeIds, networkCanonical, removedNodeIds, onFetch }) {
  const [tree, setTree] = useState(null);
  const [error, setError] = useState(null);
  // last-fetch timestamp is owned by the parent (App) now — it renders the
  // status legend in the app-wide bottom bar; we just report each fetch.
  // decommission slug: filter recently-removed node ids out of the tree
  // for rendering, so a fresh node-delete disappears immediately even
  // while qbric's topology snapshot still includes it (qbric refreshes
  // on its cleanup-interval, ~60s). App tracks the ids; the filter
  // is a no-op once qbric catches up.
  const displayTree = useMemo(() => {
    if (!tree || !removedNodeIds || removedNodeIds.size === 0) return tree;
    const filter = (n) => {
      if (!n) return null;
      if (removedNodeIds.has(n.nodeId)) return null;
      return { ...n, children: (n.children || []).map(filter).filter(Boolean) };
    };
    return filter(tree);
  }, [tree, removedNodeIds]);
  // protected-asset-indicator slug: lock overlay on access-controlled nodes.
  const { isProtected } = useProtectedResources();

  // Per-network-topology-source slug: when a non-legacy network is
  // selected, fetch its OWN qbric's topology by passing the canonical_name
  // to the BFF. Aveq-1 / aveq stay on root-qbric (the legacy default,
  // hosted by the existing root-qbric service).
  useEffect(() => {
    let cancelled = false;
    const fetchTree = async () => {
      try {
        const url = networkCanonical
          ? `/api/topology?network=${encodeURIComponent(networkCanonical)}`
          : '/api/topology';
        const r = await fetch(url);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) {
          setTree(data);
          setError(null);
          if (onFetch) onFetch(new Date());
        }
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    };
    fetchTree();
    const id = setInterval(fetchTree, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [networkCanonical]);

  // Auto-probe: for every node the API reports as live=false, send a
  // background probe. If it returns OK, mark the node as probed-OK so
  // the topology paints it green and NodeDetail's `live` row says
  // `true (via probe)`. Throttled to once per PROBE_REFRESH_MS even
  // though /api/topology polls every 5s — without the throttle, each
  // tree-refresh would re-fire the probes immediately.
  const lastProbeAt = useRef(0);
  useEffect(() => {
    if (!tree || !onProbeOk) return;
    let cancelled = false;
    const tickProbes = async () => {
      const now = Date.now();
      if (now - lastProbeAt.current < PROBE_REFRESH_MS) return;
      lastProbeAt.current = now;
      const stale = [];
      const walk = (n) => {
        if (!n) return;
        // Pending nodes (topology-pending-nodes slug) are hub-side
        // identities not yet connected to the mesh — probing them is
        // wasted (the routing layer doesn't know them yet). Skip.
        if (n.live === false && !n.pending) stale.push(n.nodeId);
        (n.children || []).forEach(walk);
      };
      walk(tree);
      // First-probe-after-refresh sometimes fails for downstream nodes
      // (route warm-up via rendezvous). Retry once after a short delay
      // so the user doesn't have to manually click for the first paint.
      const tryProbe = async (nodeId) => {
        try {
          const probeUrl = networkCanonical
            ? `/api/probe?network=${encodeURIComponent(networkCanonical)}`
            : '/api/probe';
          const r = await fetch(probeUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ target_node_id: nodeId }),
          });
          const text = await r.text();
          let parsed = null;
          try { parsed = JSON.parse(text); } catch (_) {}
          // Probe is logically successful only if HTTP is ok AND the
          // payload's first item has ok=true. Without checking the
          // payload, an HTTP-200 envelope with an `ok:false` body would
          // be treated as success.
          const innerOk = !!(parsed && parsed.payload && parsed.payload[0] && parsed.payload[0].ok);
          return r.ok && innerOk;
        } catch (_) { return false; }
      };
      const remaining = [];
      for (const nodeId of stale) {
        if (cancelled) return;
        const ok = await tryProbe(nodeId);
        if (cancelled) return;
        if (ok) onProbeOk(nodeId);
        else remaining.push(nodeId);
      }
      if (remaining.length) {
        await new Promise((r) => setTimeout(r, 3000));
        for (const nodeId of remaining) {
          if (cancelled) return;
          const ok = await tryProbe(nodeId);
          if (cancelled) return;
          if (ok) onProbeOk(nodeId);
        }
      }
    };
    tickProbes();
    const id = setInterval(tickProbes, PROBE_REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [tree, onProbeOk]);

  if (error) return <div style={{ padding: 20, color: '#c00' }}>Error: {error}</div>;
  if (!displayTree) return <div style={{ padding: 20, color: '#888' }}>Loading…</div>;

  // Per-network filter: when a network's root_node_id + node-ids set
  // are passed in, narrow the rendered tree to (a) the subtree under
  // root_node_id and (b) only nodes whose id is in the membership set.
  // Both args null → full graph (backward-compat default).
  let rendered = displayTree;
  if (rootNodeId != null) {
    // Pass rootNodeId as-is (string from list_networks's text cast).
    // Coercing to Number() here would round any id > 2^53 down to the
    // nearest representable double — exactly the bug findSubtree's
    // String() compare was meant to dodge.
    const sub = findSubtree(displayTree, rootNodeId);
    if (!sub) {
      return (
        <div style={{ padding: 20, color: '#888', fontSize: '0.9rem' }}>
          Network's root node ({rootNodeId}) isn't visible in the topology yet.
          The 5s poll should pick it up; if not, the root may not be linked under root-qbric.
        </div>
      );
    }
    if (nodeIds && nodeIds.length) {
      // Set membership in O(1); also handles legacy `aveq-1` where the
      // root_node_id is the global mesh root and the subtree visually
      // contains every other network's branches.
      const memberSet = new Set(nodeIds.map(String));
      rendered = pruneToNetwork(sub, memberSet);
      if (!rendered) {
        return (
          <div style={{ padding: 20, color: '#888', fontSize: '0.9rem' }}>
            Network's root node ({rootNodeId}) isn't in its own membership
            set — likely a stale cache or a mid-flight rename.
            Refresh the Networks tab.
          </div>
        );
      }
    } else {
      rendered = sub;
    }
  }

  const laid = computeLayout(rendered);
  const { nodes, links } = flattenTree(laid, 0);
  const xs = nodes.map((n) => n.x);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const width = Math.max(800, maxX - minX + 240);
  const maxY = Math.max(...nodes.map((n) => n.y));
  const height = maxY + 140;
  const xOffset = -minX + 120;
  const yOffset = 60;

  return (
    <div>
      {/* Top-level + Add Node button removed in multi-network slug
          Phase 4 — new nodes are added per-parent via NodeDetail's
          hamburger menu (Add Child Node). Avoids the orphan/root
          ambiguity and makes parent inheritance of network_id
          unambiguous server-side. */}
      <svg width={width} height={height} style={{ display: 'block' }}>
        {links.map((l, i) => (
          <line key={`l${i}`}
            x1={l.x1 + xOffset} y1={l.y1 + yOffset}
            x2={l.x2 + xOffset} y2={l.y2 + yOffset}
            stroke="#bbb" strokeWidth={1.5} />
        ))}
        {nodes.map((n) => {
          const isSelected = n.nodeId === selectedId;
          // indirect-liveness slug: liveness via rendezvous session presence
          // (stamped by the BFF /api/topology from each node's bound session),
          // not root-qbric's next-hop proxy — so an offline multi-hop node no
          // longer paints green just because its parent is up. A successful
          // in-session probe still overrides a false red. (Mirrors the footer
          // legend below.)
          const isStale = n.live === false && !(probedOkIds && probedOkIds.has(n.nodeId));
          // Pending (topology-pending-nodes slug): hub-side identity that
          // hasn't connected to the mesh yet. Treated as its own visual
          // state — neither stale-red nor live-green — so the operator
          // knows the add landed but is still waiting for the recipient.
          const isPending = n.pending === true;
          // indirect-liveness slug: amber = unconfirmed (rendezvous
          // unreachable) — matching the footer legend below. The BFF saw a
          // partial pool view, so it left liveness unverified
          // (`liveness_degraded`) rather than trusting the green/red; render
          // as its own amber "unknown" state. Pending takes precedence (a
          // pending node is known-not-yet-connected, not "unknown").
          const isDegraded = n.liveness_degraded === true && !isPending;
          // lite-node slug Phase 5: render lite-capable nodes with a
          // distinct gold-leaf border so operators see what's enveloped
          // end-to-end and what isn't, without obscuring stale / live /
          // pending state (those drive the fill).
          const isLite = n.lite === true;
          // Fill precedence: pending (gray) > degraded (amber/unknown) >
          // stale (red) > live (green).
          const baseFill = isPending ? '#e8e8e8' : (isDegraded ? '#f0d890' : (isStale ? '#f08080' : '#5fa67a'));
          const brightFill = isPending ? '#f5f5f5' : (isDegraded ? '#ffe9a8' : (isStale ? '#ffb8b8' : '#8acca0'));
          const strokeColor = isPending ? '#999' : (isLite ? '#b08436' : '#222');
          const strokeWidth = isSelected ? 3 : (isLite ? 2.5 : 1.5);
          return (
            <g key={n.nodeId}
               onClick={() => onSelect(n.nodeId)}
               style={{ cursor: 'pointer' }}>
              {isPending && <title>Provisioned — waiting for first connection. Run the installer on the recipient, or start the spawned container, to bring it online.</title>}
              {isDegraded && !isPending && <title>Liveness unconfirmed — a rendezvous instance was unreachable, so offline status couldn't be verified. Showing the last-known value.</title>}
              {isLite && !isPending && <title>Lite node — plaintext via rendezvous; no envelope crypto. See lite-node ADR.</title>}
              <circle
                cx={n.x + xOffset} cy={n.y + yOffset} r={NODE_RADIUS}
                fill={isSelected ? brightFill : baseFill}
                stroke={strokeColor}
                strokeWidth={strokeWidth}
                strokeDasharray={isPending ? '4 3' : undefined} />
              {/* topology-drop-router-ring: the dashed inner ring on routing
                  nodes (n.router, e.g. tobin-root) was low-value visual noise —
                  removed. The pending-node dash (above) + lite gold border +
                  fill state still convey the useful signals. */}
              {isProtected && isProtected('node', n.nodeId) && (
                // protected-asset-indicator slug: a shield badge marks the
                // node as access-controlled (some chat-user role explicitly
                // grants it → shadow-allow restricts it). Centered in the
                // node, 24px (was a 16px upper-right badge). The <g> carries
                // the hover tooltip.
                <g>
                  <title>Access-controlled (protected)</title>
                  <ShieldIcon
                    x={n.x + xOffset - 12}
                    y={n.y + yOffset - 12}
                    width={24} height={24} />
                </g>
              )}
              <text x={n.x + xOffset} y={n.y + yOffset + NODE_RADIUS + 16}
                    textAnchor="middle" fontSize={12} fill="#333">{n.name}</text>
              <text x={n.x + xOffset} y={n.y + yOffset + NODE_RADIUS + 30}
                    textAnchor="middle" fontSize={10}
                    fill={isPending ? '#a06000' : (isDegraded ? '#8a6d00' : '#888')}>
                {isPending
                  ? 'pending'
                  : isDegraded
                    ? 'unknown'
                    : `${n.station_count} station${n.station_count === 1 ? '' : 's'}`}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
